/**
 * Insight readiness tiers:
 *   Tier 1 "Early Patterns": mealsCount >= 5 AND poopsCount >= 3
 *   Tier 2 "Full Analysis":  mealsCount >= 30 AND poopsCount >= 20
 *
 * Legacy threshold kept for backward compat.
 */

export const TIER1_MEALS = 5;
export const TIER1_POOPS = 3;
export const TIER2_MEALS = 30;
export const TIER2_POOPS = 20;

export function getInsightTier({ mealsCount = 0, poopsCount = 0 } = {}) {
  if (mealsCount >= TIER2_MEALS && poopsCount >= TIER2_POOPS) return 'full';
  if (mealsCount >= TIER1_MEALS && poopsCount >= TIER1_POOPS) return 'preliminary';
  return 'none';
}

/**
 * Legacy check — kept for backward compatibility.
 */
export function isReadyForInsights({ mealsCount = 0, poopsCount = 0, daysCovered = 0 } = {}) {
  return (
    (mealsCount >= 3 && poopsCount >= 2) ||
    (daysCovered >= 2 && mealsCount >= 2 && poopsCount >= 1)
  );
}

/**
 * Progress toward each tier.
 */
export function getProgress({ mealsCount = 0, poopsCount = 0 } = {}) {
  const tier1MealPct = Math.min(mealsCount / TIER1_MEALS, 1);
  const tier1PoopPct = Math.min(poopsCount / TIER1_POOPS, 1);
  const tier2MealPct = Math.min(mealsCount / TIER2_MEALS, 1);
  const tier2PoopPct = Math.min(poopsCount / TIER2_POOPS, 1);

  return {
    tier1: Math.round((tier1MealPct * 0.6 + tier1PoopPct * 0.4) * 100),
    tier2: Math.round((tier2MealPct * 0.6 + tier2PoopPct * 0.4) * 100),
    mealsCount,
    poopsCount,
  };
}

/**
 * Get milestone message if the user just hit a notable threshold.
 */
export function getMilestoneMessage(mealsCount, poopsCount) {
  const milestones = [
    { meals: 5, poops: 3, msg: 'Early patterns unlocked! Check your Insights.' },
    { meals: 10, poops: null, msg: '10 meals logged — your patterns are getting clearer!' },
    { meals: 15, poops: null, msg: '15 meals! Halfway to full analysis.' },
    { meals: 20, poops: null, msg: '20 meals — patterns are becoming more reliable.' },
    { meals: 25, poops: null, msg: 'Almost there! 5 more meals to full analysis.' },
    { meals: 30, poops: 20, msg: 'Full analysis unlocked! Your data is ready for deep insights.' },
  ];

  for (const m of milestones) {
    if (m.meals === mealsCount && (m.poops === null || m.poops === poopsCount)) {
      return m.msg;
    }
  }
  return null;
}

/**
 * Returns a progress-aware message after logging, or null if no message needed.
 */
export function getProgressMessage(logType, { mealsCount = 0, poopsCount = 0, daysCovered = 0 } = {}, hasAnalyzed = false) {
  // Check for milestone first
  const milestone = getMilestoneMessage(mealsCount, poopsCount);
  if (milestone) return milestone;

  if (hasAnalyzed) return null;

  const tier = getInsightTier({ mealsCount, poopsCount });
  if (tier === 'full') return 'Full analysis ready! Head to Insights for deep insights.';
  if (tier === 'preliminary') return null; // Already have preliminary, no nagging

  // Progress toward tier 1
  const mealsNeeded = Math.max(0, TIER1_MEALS - mealsCount);
  const poopsNeeded = Math.max(0, TIER1_POOPS - poopsCount);
  const totalNeeded = mealsNeeded + poopsNeeded;

  if (totalNeeded <= 2 && totalNeeded > 0) {
    const parts = [];
    if (mealsNeeded > 0) parts.push(`${mealsNeeded} more meal${mealsNeeded > 1 ? 's' : ''}`);
    if (poopsNeeded > 0) parts.push(`${poopsNeeded} more poop${poopsNeeded > 1 ? 's' : ''}`);
    return `Almost there! Just ${parts.join(' and ')} to unlock early patterns`;
  }

  if (logType === 'meal') {
    return `Logged! ${mealsCount} of ${TIER1_MEALS} meals toward your first insight`;
  }
  return `Logged! ${poopsCount} of ${TIER1_POOPS} poops toward your first insight`;
}
